import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CheckPermissions, {
    hasServiceLevelPermission,
} from "../../authorization/authorization";

/**
 * Mapping of table routes to their corresponding form paths and required permissions.
 * @constant {Object.<string, {path: string, module?: string[], modules?: string[][], serviceCreate?: string|string[]}>}
 */
const permissionMapping = {
    "/new-connection-table": { path: "/new-connection-form", module: ["General Services", "New Connection", "Basic Permissions", "Create"] },
    "/reconnection-table": { path: "/reconnection-form", module: ["General Services", "Reconnection", "Basic Permissions", "Create"] },
    "/bill-correction-table": { path: "/bill-correction-form", module: ["Billing and Collection", "Bill Correction", "Basic Permissions", "Create"] },
    "/change-of-attributes-table": { path: "/change-of-attributes-form/name", module: ["General Services", "Change of Attributes", "Basic Permissions", "Create"] },
    "/change-of-load-table": { path: "/change-of-attributes-form/load", module: ["General Services", "Change of Load", "Basic Permissions", "Create"] },
    "/change-of-tariff-table": { path: "/change-of-attributes-form/tariff", module: ["General Services", "Change of Tariff", "Basic Permissions", "Create"] },
    "/net-metering": { path: "/net-metering/net-metering-form", module: ["General Services", "Shift to Net Meter", "Basic Permissions", "Create"] },
    "/meter-change-request-table": { path: "/meter-change-request-form", module: ["General Services", "Meter Change Order", "Basic Permissions", "Create"] },
    "/meter-reading-correction-table": {
        path: "/meter-reading-correction-form",
        serviceCreate: [
            "Meter Reading Correction",
            "Meter Reading Corrections",
        ],
    },
    "/due-date-extension-table": { path: "/due-date-extension-form", module: ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"] },
    "/adjust-last-bill-table": {
        path: "/adjust-last-bill-form",
        modules: [
            ["General Services", "Adjust Last Bill", "Basic Permissions", "Create"],
            ["Billing and Collection", "Adjust Last Bill", "Basic Permissions", "Create"],
            ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"],
        ],
    },
    "/amend-2636-bill-table": { path: "/amend-2636-bill-form", module: ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"] },
    "/advance-payment-table": {
        path: "/advance-payment-form",
        modules: [
            ["Billing and Collection", "Due Date Extension", "Basic Permissions", "Create"],
            ["General Services", "Advance Payment", "Basic Permissions", "Create"],
            ["General Services", "Part Payment", "Basic Permissions", "Create"],
        ],
    },
    "/installments-table": { path: "/installments-form", module: ["Billing and Collection", "Installments", "Basic Permissions", "Create"] },
    "/set-aside-table": { path: "/set-aside-form", module: ["Billing and Collection", "Set Aside", "Basic Permissions", "Create"] },
    "/power-complaint-table": { path: "/power-complaint-form", module: ["Power Complaints"] },
    "/user-management": { path: "/user-management/new-user", module: ["Access Management", "User Management", "CSD Management", "Create"] },
    "/role-management": { path: "/role-management/new-role", module: ["Access Management", "Role Management", "Create"] },
    "/permission-management": { path: "/permission-management/new-permission", module: ["Access Management", "Permission Management", "Create"] },
    "/company-management": { path: "/company-management/new-company", module: ["Organization Management", "Company Management", "Create"] },
    "/circle-management": { path: "/circle-management/new-circle", module: ["Organization Management", "Circle Management", "Create"] },
    "/division-management": { path: "/division-management/new-division", module: ["Organization Management", "Division Management", "Create"] },
    "/support-center-management": { path: "/support-center-management/new-support-center", module: ["Organization Management", "CSC Management", "Create"] },
    "/sub-division-management": { path: "/sub-division-management/new-sub-division", module: ["Organization Management", "Sub Division Management", "Create"] },
};

const normalizePath = (pathname = "") => pathname.replace(/\/$/, "") || "/";

const resolvePermissionDetails = (pathname) => {
    const currentPage = normalizePath(pathname);
    if (permissionMapping[currentPage]) return permissionMapping[currentPage];

    const match = Object.entries(permissionMapping).find(([path]) =>
        currentPage.endsWith(path)
    );
    return match?.[1] || null;
};

const hasPermission = (permissions, moduleHierarchy) => {
    if (!permissions || !moduleHierarchy) return false;
    try {
        return CheckPermissions(permissions, ...moduleHierarchy);
    } catch {
        return false;
    }
};

/** True only when Create exists under the given MRC-like service. */
const hasCreateForService = (permissions, serviceName) => {
    if (!permissions?.length || !serviceName) return false;
    try {
        return (
            hasServiceLevelPermission(
                permissions,
                serviceName,
                "Basic Permissions",
                "Create"
            ) ||
            hasServiceLevelPermission(permissions, serviceName, "Create") ||
            CheckPermissions(
                permissions,
                "Billing and Collection",
                serviceName,
                "Basic Permissions",
                "Create"
            ) ||
            CheckPermissions(
                permissions,
                "General Services",
                serviceName,
                "Basic Permissions",
                "Create"
            ) ||
            CheckPermissions(
                permissions,
                "Commercial Operations",
                serviceName,
                "Basic Permissions",
                "Create"
            )
        );
    } catch {
        return false;
    }
};

const NewButton = ({ onClick }) => (
    <button
        type="button"
        className="header-add-btn navbar-new-btn"
        onClick={onClick}
        aria-label="New"
    >
        <AddIcon className="navbar-new-btn__icon" />
        <span className="navbar-new-btn__label">New</span>
    </button>
);

/**
 * A button component that navigates to a form page based on the current route,
 * only if the user has the required permissions.
 */
const AddButton = ({ permissions, isButtonVisible }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const permissionDetails = resolvePermissionDetails(location.pathname);

    const isAuthorized = useMemo(() => {
        if (!permissionDetails) return false;

        if (permissionDetails.serviceCreate) {
            const serviceNames = Array.isArray(permissionDetails.serviceCreate)
                ? permissionDetails.serviceCreate
                : [permissionDetails.serviceCreate];

            return serviceNames.some((serviceName) =>
                hasCreateForService(permissions, serviceName)
            );
        }

        if (permissionDetails.modules?.length) {
            return permissionDetails.modules.some((moduleHierarchy) =>
                hasPermission(permissions, moduleHierarchy)
            );
        }

        return hasPermission(permissions, permissionDetails.module);
    }, [permissions, permissionDetails]);

    if (!permissionDetails || !isAuthorized) return null;

    // For MRC, Create permission alone is enough to show New (sidebar gate can miss nested trees)
    if (permissionDetails.serviceCreate) {
        return <NewButton onClick={() => navigate(permissionDetails.path)} />;
    }

    if (!isButtonVisible) return null;

    return <NewButton onClick={() => navigate(permissionDetails.path)} />;
};

export default AddButton;
